import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, ListFilter, Loader2, Lock } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/cn';
import type { IntegrationMode } from '@/lib/providers/types';
import { MODE_TONE } from './mode-tone';
import { modeWarning } from './format';

/**
 * The building blocks every service card on Proveedores is made of: the card
 * frame (title · "N in use · M total" · what the service is for · how its
 * providers combine), the compact provider table, one expandable row, the mode
 * chip and the status cell. Presentational only — the rows decide WHAT to say
 * (`integrations-card.tsx`, `flight-info.tsx`), these decide how it looks.
 *
 * At phone width the table scrolls sideways INSIDE its card (never the page),
 * and every control in it is at least 44px tall.
 */

/* ────────────────────────────── Service card ─────────────────────────────── */

interface ServiceCardProps {
  testId: string;
  title: string;
  summary: string;
  summaryTestId: string;
  description?: string | undefined;
  /** The strategy chip(s) on the right of the header. */
  chips?: ReactNode;
  /** Thin bars under the header: the "Order of use" lines, a section notice. */
  bars?: ReactNode;
  footer?: ReactNode;
  hidden?: boolean | undefined;
  selection?: string | undefined;
  children?: ReactNode;
}

export function ServiceCard({
  testId,
  title,
  summary,
  summaryTestId,
  description,
  chips,
  bars,
  footer,
  hidden,
  selection,
  children,
}: ServiceCardProps) {
  const headingId = useId();
  return (
    <Card
      className="overflow-hidden"
      data-testid={testId}
      data-selection={selection}
      hidden={hidden}
      role="region"
      aria-labelledby={headingId}
    >
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 border-b px-4 py-3">
        <div className="min-w-0 flex-1 basis-72">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
            <h2 id={headingId} className="text-base font-semibold">
              {title}
            </h2>
            <span className="text-xs tabular-nums text-muted-foreground" data-testid={summaryTestId}>
              {summary}
            </span>
          </div>
          {description ? <p className="mt-0.5 text-sm text-muted-foreground">{description}</p> : null}
        </div>
        {chips ? <div className="flex flex-wrap items-center gap-2">{chips}</div> : null}
      </div>
      {bars}
      {children}
      {footer}
    </Card>
  );
}

/** How the providers of a card combine — read-only form of the header chip. */
export function StrategyChip({
  children,
  title,
  testId,
}: {
  children: ReactNode;
  title?: string | undefined;
  testId?: string | undefined;
}) {
  return (
    <span
      className="inline-flex min-h-8 items-center gap-1.5 whitespace-nowrap rounded-md border bg-muted/40 px-2.5 py-1 text-xs"
      title={title}
      data-testid={testId}
    >
      <ListFilter className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
      {children}
    </span>
  );
}

/** A one-line notice under a card header ("nothing enabled in this category"). */
export function CardNotice({ children, testId }: { children: ReactNode; testId?: string }) {
  return (
    <p
      className="border-b bg-sky-50 px-4 py-2 text-sm text-sky-900 dark:bg-sky-950/40 dark:text-sky-200"
      data-testid={testId}
    >
      {children}
    </p>
  );
}

/** The muted line under a table: providers also available but not used here. */
export function CardFooterLine({ children, testId }: { children: ReactNode; testId?: string }) {
  return (
    <p className="border-t px-4 py-2.5 text-xs text-muted-foreground" data-testid={testId}>
      {children}
    </p>
  );
}

/* ──────────────────────────────── The table ──────────────────────────────── */

/** Columns: # · provider · mode (chip + «fijo», or the mode choice) · status · used for · in use. */
export function ProviderTable({ ordered, children }: { ordered: boolean; children: ReactNode }) {
  const { t } = useTranslation('network');
  const th = 'px-3 py-2 text-left font-semibold';
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[840px] table-fixed border-collapse text-sm">
        <colgroup>
          <col className="w-11" />
          <col />
          {/* Room for the mode choice (Real · Sintético) — M2. */}
          <col className="w-36" />
          <col className="w-[24%]" />
          <col className="w-[17%]" />
          <col className="w-44" />
        </colgroup>
        <thead className="border-b bg-muted/40 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
          <tr>
            <th scope="col" className={th}>
              {ordered ? (
                <span title={t('providers.table.positionHint')}>{t('providers.table.order')}</span>
              ) : (
                <span className="sr-only">{t('providers.table.order')}</span>
              )}
            </th>
            <th scope="col" className={th}>
              {t('providers.table.provider')}
            </th>
            <th scope="col" className={th}>
              {t('providers.table.mode')}
            </th>
            <th scope="col" className={th}>
              {t('providers.table.status')}
            </th>
            <th scope="col" className={th}>
              {t('providers.table.usedFor')}
            </th>
            <th scope="col" className={cn(th, 'text-right')}>
              {t('providers.table.inUse')}
            </th>
          </tr>
        </thead>
        {children}
      </table>
    </div>
  );
}

interface ProviderRowProps {
  /** On the row's `<tbody>`, which holds the line AND its detail. */
  testId: string;
  dataState?: string | undefined;
  dataMode?: string | undefined;
  dataHealthSource?: string | undefined;
  hidden?: boolean | undefined;
  /** Switched off: the line reads dimmed (its detail does not). */
  dimmed?: boolean | undefined;
  position?: number | undefined;
  /** Its place is fixed (the airline always wins): the why, for the lock's hover and a screen reader. */
  locked?: string | undefined;
  name: string;
  /** The raw token, for a hover — never the visible name. */
  nameTitle?: string | undefined;
  /** The muted second line: declaring service, channel, source. */
  sub?: string | undefined;
  expandTestId: string;
  mode: ReactNode;
  status: ReactNode;
  usedFor: ReactNode;
  inUse: ReactNode;
  /** A warning to read WITHOUT opening the row: a full-width line right under it. */
  notice?: ReactNode;
  detailTestId: string;
  /** Rendered only while open. `nameId` names the row, for the controls inside. */
  renderDetail: (ids: { nameId: string }) => ReactNode;
}

/**
 * One provider: a compact line, and — when its name is pressed — a detail panel
 * right under it. Each row opens on its own; several may be open at once.
 */
export function ProviderRow({
  testId,
  dataState,
  dataMode,
  dataHealthSource,
  hidden,
  dimmed,
  position,
  locked,
  name,
  nameTitle,
  sub,
  expandTestId,
  mode,
  status,
  usedFor,
  inUse,
  notice,
  detailTestId,
  renderDetail,
}: ProviderRowProps) {
  const [open, setOpen] = useState(false);
  const nameId = useId();
  const detailId = useId();
  const td = 'px-3 py-1 align-middle';

  return (
    <tbody
      className="border-t"
      data-testid={testId}
      data-state={dataState}
      data-mode={dataMode}
      data-health-source={dataHealthSource}
      data-open={open}
      hidden={hidden}
    >
      <tr className={cn(dimmed && 'opacity-60')}>
        <td className={cn(td, 'font-mono text-xs tabular-nums text-muted-foreground')}>
          {position ?? ''}
          {locked ? (
            <span className="ml-0.5 inline-flex align-middle" title={locked} data-testid={`${testId}-locked`}>
              <Lock className="h-3 w-3" aria-hidden />
              <span className="sr-only">{locked}</span>
            </span>
          ) : null}
        </td>
        <td className={td}>
          <button
            type="button"
            id={nameId}
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls={open ? detailId : undefined}
            className="flex min-h-11 w-full min-w-0 items-center gap-2 rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            data-testid={expandTestId}
          >
            <ChevronRight
              className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-90')}
              aria-hidden
            />
            <span className="flex min-w-0 flex-col">
              <span className="truncate font-medium" title={nameTitle} data-testid={`${testId}-name`}>
                {name}
              </span>
              {sub ? <span className="truncate text-xs text-muted-foreground">{sub}</span> : null}
            </span>
          </button>
        </td>
        <td className={td}>{mode}</td>
        <td className={td}>{status}</td>
        <td className={cn(td, 'break-words text-xs text-muted-foreground')}>{usedFor}</td>
        <td className={cn(td, 'text-right')}>{inUse}</td>
      </tr>
      {notice ? (
        <tr>
          <td colSpan={6} className="px-3 pb-2 pt-0 sm:pl-14">
            {notice}
          </td>
        </tr>
      ) : null}
      {open ? (
        <tr>
          <td
            colSpan={6}
            id={detailId}
            className="bg-muted/40 px-3 pb-4 pt-2 sm:pl-14"
            data-testid={detailTestId}
          >
            {renderDetail({ nameId })}
          </td>
        </tr>
      ) : null}
    </tbody>
  );
}

/* ─────────────────────────────── Cells ───────────────────────────────────── */

/**
 * REAL · SANDBOX · SYNTHETIC — the safety affordance of the row: the real
 * thing reads red, a vendor's test environment amber, a synthetic provider
 * (answers inside the platform) violet. `null` mode = nothing runs: a dash.
 * The hover says what the mode means for `category` (real MESSAGES for
 * notifications, real bookings and charges for hotels — see `modeWarning`).
 */
export function ModeChip({
  mode,
  category,
  testId,
}: {
  mode: IntegrationMode | null;
  category?: string;
  testId?: string;
}) {
  const { t } = useTranslation('network');
  if (!mode) {
    return (
      <span className="text-muted-foreground">
        <span aria-hidden>—</span>
        <span className="sr-only">{t('providers.table.noMode')}</span>
      </span>
    );
  }
  return (
    <span
      className={cn(
        'inline-flex whitespace-nowrap rounded-md border px-2 py-0.5 font-mono text-[11px] font-semibold uppercase tracking-wider',
        MODE_TONE[mode],
      )}
      title={modeWarning(t, mode, category)}
      data-testid={testId}
      data-mode={mode}
    >
      {t(`providers.integrations.mode.${mode}`)}
    </span>
  );
}

/** The colour family of a status: one dot, one label tone. */
export type StatusTone = 'ok' | 'fail' | 'checking' | 'synthetic' | 'neutral' | 'warn' | 'off' | 'unknown';

const DOT: Record<Exclude<StatusTone, 'checking'>, string> = {
  ok: 'bg-emerald-500',
  fail: 'bg-rose-500',
  synthetic: 'bg-violet-500',
  neutral: 'border-2 border-dashed border-muted-foreground/70',
  warn: 'bg-amber-500',
  off: 'bg-muted-foreground/40',
  unknown: 'border-2 border-dashed border-amber-500',
};

const LABEL_TONE: Record<StatusTone, string> = {
  ok: 'text-emerald-700 dark:text-emerald-400',
  fail: 'text-rose-700 dark:text-rose-400',
  checking: '',
  synthetic: 'text-violet-700 dark:text-violet-300',
  neutral: '',
  warn: 'text-amber-700 dark:text-amber-400',
  off: 'text-muted-foreground',
  unknown: 'text-amber-700 dark:text-amber-400',
};

export function StatusCell({
  tone,
  label,
  hint,
  state,
  labelTestId,
  hintTestId,
}: {
  tone: StatusTone;
  label: string;
  hint?: ReactNode;
  state?: string | undefined;
  labelTestId?: string | undefined;
  hintTestId?: string | undefined;
}) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      {tone === 'checking' ? (
        <Loader2 className="h-3 w-3 shrink-0 animate-spin text-muted-foreground" aria-hidden />
      ) : (
        <span aria-hidden className={cn('inline-block h-2.5 w-2.5 shrink-0 rounded-full', DOT[tone])} />
      )}
      <div className="min-w-0">
        <p className={cn('truncate font-medium', LABEL_TONE[tone])} data-testid={labelTestId} data-state={state}>
          {label}
        </p>
        {hint ? (
          <p className="break-words text-xs text-muted-foreground" data-testid={hintTestId}>
            {hint}
          </p>
        ) : null}
      </div>
    </div>
  );
}

/** A short muted line in the "in use" column when there is no switch to show. */
export function InUseNote({ children, testId }: { children: ReactNode; testId?: string }) {
  return (
    <span className="text-xs text-muted-foreground" data-testid={testId}>
      {children}
    </span>
  );
}

/* ─────────────────────────────── Detail ──────────────────────────────────── */

/** The detail panel: facts on the left, actions (probe, order) on the right. */
export function DetailLayout({ facts, actions }: { facts: ReactNode; actions?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-start gap-x-8 gap-y-4">
      <dl className="grid min-w-0 flex-1 basis-80 grid-cols-[max-content_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-xs">
        {facts}
      </dl>
      {actions ? <div className="w-full min-w-0 space-y-3 sm:w-72">{actions}</div> : null}
    </div>
  );
}

export function DetailItem({
  label,
  children,
  testId,
  className,
}: {
  label: string;
  children: ReactNode;
  testId?: string | undefined;
  className?: string | undefined;
}) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={cn('min-w-0 break-words', className)} data-testid={testId}>
        {children}
      </dd>
    </>
  );
}
